import React, { useEffect, useId, useRef, useState } from 'react';
import './CharacterDraftSelector.css';

export default function CharacterDraftSelector({ drafts, selectedDraftId, onSelect, onAction,
    buildName, onDelete, pending, catalogReady, error, notice, onCancelLoad, menuStyle = false }) {
    const id = useId();
    const [open, setOpen] = useState(false);
    const [saveOpen, setSaveOpen] = useState(false);
    const [saveName, setSaveName] = useState('');
    const [highlight, setHighlight] = useState(0);
    const [deleteId, setDeleteId] = useState(null);
    const loadButton = useRef(null);
    const loadWrap = useRef(null);
    const highlighted = useRef(null);
    const saveButton = useRef(null);
    const saveInput = useRef(null);
    const returnSaveFocus = useRef(false);
    useEffect(() => {
        if (!open) return;
        const dismiss = event => {
            if (!loadWrap.current?.contains(event.target)) { setOpen(false); setDeleteId(null); }
        };
        document.addEventListener('pointerdown', dismiss);
        return () => document.removeEventListener('pointerdown', dismiss);
    }, [open]);
    useEffect(() => {
        if (open && !pending) highlighted.current?.querySelector('button')?.focus();
    }, [open, pending]);
    useEffect(() => { if (saveOpen) { saveInput.current?.focus(); saveInput.current?.select(); } }, [saveOpen]);
    useEffect(() => {
        if (!saveOpen && !pending && returnSaveFocus.current) {
            returnSaveFocus.current = false;
            saveButton.current?.focus();
        }
    }, [saveOpen, pending]);
    useEffect(() => { setHighlight(index => Math.min(index, drafts.length)); }, [drafts.length]);
    useEffect(() => {
        if (open) highlighted.current?.scrollIntoView?.({ block: 'nearest' });
    }, [open, highlight]);
    const target = drafts.find(draft => Number(draft.draftId) === Number(selectedDraftId));
    const selectedIndex = drafts.indexOf(target) + 1;
    const choose = index => {
        if (pending || !catalogReady || (index !== 0 && !drafts[index - 1])) return;
        loadButton.current?.focus();
        onSelect(index === 0 ? null : drafts[index - 1].draftId);
        setOpen(false);
        setDeleteId(null);
    };
    const closeSave = () => { returnSaveFocus.current = true; setSaveOpen(false); };
    const save = async () => {
        if (pending || !catalogReady) return;
        if (await onAction('create', saveName.trim())) closeSave();
    };
    const loadControl = (
        <div ref={loadWrap} className="draft-load-wrap" onBlur={event => {
            if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget)) { setOpen(false); setDeleteId(null); }
        }}>
            <button ref={loadButton} type="button" className="draft-save-button" disabled={pending || !catalogReady}
                aria-haspopup="grid" aria-expanded={open && !pending} aria-controls={`${id}-list`}
                onClick={() => { setSaveOpen(false); setOpen(value => !value); setDeleteId(null); setHighlight(selectedIndex); }}
                onKeyDown={event => {
                    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                        event.preventDefault(); setSaveOpen(false); setHighlight(selectedIndex); setOpen(true);
                    }
                }}>{menuStyle && <svg aria-hidden="true" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M3 6h7l2 2h9v11H3Z" /></svg>}{menuStyle ? 'Load draft…' : 'Load Draft'}</button>
            {open && !pending && <div className="combo-panel draft-build-list" id={`${id}-list`} role="grid" aria-label="Load draft"
                onKeyDown={event => {
                    if (event.key === 'Escape') {
                        event.preventDefault();
                        if (deleteId != null) { setDeleteId(null); highlighted.current?.querySelector('button')?.focus(); }
                        else { setOpen(false); loadButton.current?.focus(); }
                    } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
                        event.preventDefault();
                        const index = event.key === 'Home' ? 0 : event.key === 'End' ? drafts.length :
                            Math.max(0, Math.min(drafts.length, highlight + (event.key === 'ArrowDown' ? 1 : -1)));
                        setHighlight(index);
                        event.currentTarget.querySelectorAll('.draft-build-name')[index]?.focus();
                    }
                }}>
                <div role="row" ref={highlight === 0 ? highlighted : null} aria-selected={selectedDraftId == null}
                    className={`combo-option draft-build-row draft-new-row ${highlight === 0 ? 'is-highlighted' : ''}`}
                    onFocus={() => setHighlight(0)} onMouseEnter={() => setHighlight(0)}>
                    <div role="gridcell" id={`${id}-0`}>
                        <button type="button" className="draft-build-name" onClick={() => choose(0)}>New Draft</button>
                    </div>
                </div>
                {drafts.map((draft, index) => <div key={draft.draftId} role="row"
                    ref={highlight === index + 1 ? highlighted : null}
                    aria-selected={draft === target} className={`combo-option draft-build-row ${highlight === index + 1 ? 'is-highlighted' : ''}`}
                    onFocus={() => setHighlight(index + 1)} onMouseEnter={() => setHighlight(index + 1)}>
                    <div role="gridcell" id={`${id}-${index + 1}`}>
                        <button type="button" className="draft-build-name" aria-label={`Load build “${draft.name}”`}
                            onClick={() => choose(index + 1)} title={draft.name}>{draft.name}</button>
                    </div>
                    <div role="gridcell" className="draft-delete-cell">
                        <button type="button" className={`draft-trash ${deleteId === draft.draftId ? 'is-confirming' : ''}`}
                            aria-label={deleteId === draft.draftId ? `Confirm delete “${draft.name}”` : `Delete build “${draft.name}”`}
                            onClick={async () => {
                                if (deleteId !== draft.draftId) { setDeleteId(draft.draftId); return; }
                                const deleted = await onDelete(draft.draftId);
                                if (deleted) {
                                    setDeleteId(null);
                                    if (drafts.length === 1) setOpen(false);
                                    loadButton.current?.focus();
                                }
                            }}>
                            <svg aria-hidden="true" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8">
                                <path d="M3 6h18M9 6V3h6v3M6 6l1 15h10l1-15M10 10v7M14 10v7" />
                            </svg>
                            <span className="draft-delete-confirm" aria-hidden={deleteId !== draft.draftId}>Are you sure?</span>
                        </button>
                        {deleteId === draft.draftId && <button type="button" className="draft-delete-cancel"
                            aria-label={`Cancel deleting “${draft.name}”`} onClick={() => { setDeleteId(null); highlighted.current?.querySelector('button')?.focus(); }}>×</button>}
                    </div>
                </div>)}
            </div>}
        </div>
    );
    const saveControl = (
        <div className="draft-save-wrap" onBlur={event => {
            if (!pending && !event.currentTarget.contains(event.relatedTarget)) setSaveOpen(false);
        }}>
        <button ref={saveButton} type="button" className="draft-save-button" disabled={pending || !catalogReady}
            aria-expanded={saveOpen} aria-controls={`${id}-save`}
            onClick={() => {
                setOpen(false); setDeleteId(null);
                if (!saveOpen) setSaveName(buildName);
                setSaveOpen(value => !value);
            }}>{menuStyle && <svg aria-hidden="true" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M5 3h12l3 3v15H4V3ZM8 3v7h8V3M8 21v-7h8v7" /></svg>}{menuStyle ? 'Save draft' : 'Save Draft'}</button>
        {saveOpen && <div className="draft-save-flyout" id={`${id}-save`} role="group" aria-labelledby={`${id}-save-label`}
            onKeyDown={event => {
                if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); closeSave(); }
            }}>
            <label id={`${id}-save-label`} htmlFor={`${id}-save-name`}>Draft name</label>
            <input ref={saveInput} id={`${id}-save-name`} type="text" value={saveName} maxLength={255}
                placeholder="Name this draft" autoComplete="off" disabled={pending || !catalogReady}
                aria-describedby={`${id}-save-hint`} onChange={event => setSaveName(event.target.value)}
                onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); save(); } }} />
            <p id={`${id}-save-hint`} className="draft-hint">Leave blank to generate a name.</p>
            <div className="draft-actions">
                <button type="button" disabled={pending || !catalogReady} onClick={save}>Save</button>
                <button type="button" disabled={pending} onClick={closeSave}>Cancel</button>
            </div>
        </div>}
        </div>
    );
    return <div className="draft-selector">
        <div className="draft-picker-row">
            {menuStyle ? <>{saveControl}{loadControl}</> : <>{loadControl}{saveControl}</>}

        </div>
        {onCancelLoad && <div className="draft-actions"><button type="button" onClick={onCancelLoad}>Cancel load</button></div>}
        {pending && <p role="status">Working with saved builds…</p>}
        {error && <p role="alert">{error}</p>}
        {notice && <p role="status">{notice}</p>}
    </div>;
}
